import { useEffect, useState } from "react";
import { Loader2, Save, User, Github, Link as LinkIcon, Lock, Sparkles, Wand2, Bell, Cpu, Crown, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/integrations/supabase/AuthProvider";
import { useSonas } from "../SonasContext";
import { AI_MODELS } from "../mockData";
import { cn } from "@/lib/utils";

const AccountSettingsSheet = ({ onOpenChange, open }) => {
  const { user, profile, refreshProfile } = useAuth();
  const { isAccountRestricted, setIsStoreOpen } = useSonas();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: "", username: "", bio: "", website: "", github_url: "",
    default_model: "claude-airforce", auto_sync_enabled: false, notifications_enabled: true,
    ai_tone: "Professional", ai_style: "Concise", ai_instructions: ""
  });

  const isPro = profile?.is_pro === true;

  useEffect(() => {
    if (open && profile) {
      setFormData({
        full_name: profile.full_name || "",
        username: profile.username || "",
        bio: profile.bio || "",
        website: profile.website || "",
        github_url: profile.github_url || "",
        default_model: profile.default_model || "claude-airforce",
        auto_sync_enabled: profile.auto_sync_enabled || false,
        notifications_enabled: profile.notifications_enabled ?? true,
        ai_tone: profile.ai_tone || "Professional",
        ai_style: profile.ai_style || "Concise",
        ai_instructions: profile.ai_instructions || ""
      });
    }
  }, [open, profile]);

  const handleSave = async () => {
    if (isAccountRestricted) {
      toast.error("Account modifications restricted.");
      return;
    }
    try {
      setLoading(true);
      const { error } = await supabase.from('profiles').update(formData).eq('id', user.id);
      if (error) throw error;
      
      refreshProfile();
      toast.success("Preferences updated successfully.");
      onOpenChange(false);
    } catch (error) {
      toast.error("Update failed.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetContent className="glass-surface h-full border-white/5 bg-sonas-bg p-0 text-sonas-text w-full sm:max-w-md overflow-y-auto" side="right">
        <div className="flex flex-col min-h-full">
          <SheetHeader className="p-6 border-b border-white/5 bg-sonas-bg/50 backdrop-blur-xl">
            <SheetTitle className="text-2xl font-syne">Architect Workspace</SheetTitle>
            <SheetDescription className="text-sonas-muted">Manage your identity and platform behavior.</SheetDescription>
          </SheetHeader>

          {isAccountRestricted && (
            <div className="m-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl flex items-center gap-3">
              <Lock className="text-amber-500 h-4 w-4 shrink-0" />
              <p className="text-[10px] font-bold text-amber-500 uppercase">Configuration Locked by Administrator</p>
            </div>
          )}

          <div className={cn("flex-1 p-6 space-y-8 sonas-scrollbar pb-32", isAccountRestricted && "opacity-60 pointer-events-none")}>
            {/* Identity Section */}
            <section className="space-y-6">
              <div className="flex flex-col items-center gap-4">
                <Avatar className="h-20 w-20 border-2 border-sonas-accent shadow-xl shadow-sonas-accent/10">
                  <AvatarImage src={profile?.avatar_url} />
                  <AvatarFallback className="bg-sonas-surface2 text-xl font-bold">
                    {profile?.username?.charAt(0).toUpperCase() || "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="text-center">
                  <p className="font-syne font-bold text-lg">{profile?.full_name || 'Anonymous Architect'}</p>
                  <p className="text-xs text-sonas-dim font-mono">@{profile?.username || 'unidentified'}</p>
                </div>
              </div>

              {!isPro && (
                <button 
                  onClick={() => setIsStoreOpen(true)}
                  className="w-full p-4 rounded-2xl bg-sonas-accent/5 border border-sonas-accent/20 flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3">
                    <Crown className="text-sonas-accent h-5 w-5" />
                    <div className="text-left">
                      <p className="text-xs font-bold text-sonas-accent uppercase">Upgrade to Architect Pro</p>
                      <p className="text-[10px] text-sonas-muted">Unlock advanced AI personalization and sync.</p>
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-sonas-accent group-hover:translate-x-1 transition-transform" />
                </button>
              )}

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-sonas-dim uppercase tracking-widest ml-1">Display Name</label>
                  <Input 
                    value={formData.full_name} 
                    onChange={e => setFormData({...formData, full_name: e.target.value})} 
                    className="bg-sonas-surface2 border-white/5 h-11 rounded-xl" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-sonas-dim uppercase tracking-widest ml-1">Username</label>
                  <Input 
                    value={formData.username} 
                    onChange={e => setFormData({...formData, username: e.target.value.toLowerCase().replace(/\s/g, '')})} 
                    className="bg-sonas-surface2 border-white/5 h-11 rounded-xl font-mono" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-sonas-dim uppercase tracking-widest ml-1">Bio</label>
                  <Textarea 
                    value={formData.bio} 
                    onChange={e => setFormData({...formData, bio: e.target.value})} 
                    className="bg-sonas-surface2 border-white/5 rounded-xl text-xs min-h-[100px]" 
                  />
                </div>
              </div>
            </section>

            {/* AI Preferences */}
            <section className="space-y-4">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-sonas-accent flex items-center gap-2">
                <Sparkles size={12} /> Inference Settings
              </h3>
              <div className="space-y-4 bg-white/[0.02] border border-white/5 p-5 rounded-3xl">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-sonas-dim uppercase tracking-widest">Preferred Model</label>
                  <Select value={formData.default_model} onValueChange={val => setFormData({...formData, default_model: val})}>
                    <SelectTrigger className="bg-sonas-surface2 border-white/5 h-11 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-sonas-bg border-white/10">
                      {AI_MODELS.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className={cn("space-y-4", !isPro && "opacity-50 pointer-events-none")}>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-sonas-dim uppercase tracking-widest flex items-center gap-2">
                        AI Tone {!isPro && <Crown size={10} className="text-sonas-accent" />}
                      </label>
                      <Select value={formData.ai_tone} onValueChange={val => setFormData({...formData, ai_tone: val})}>
                        <SelectTrigger className="bg-sonas-surface2 border-white/5 h-10 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-sonas-bg border-white/10">
                          <SelectItem value="Professional">Professional</SelectItem>
                          <SelectItem value="Casual">Casual</SelectItem>
                          <SelectItem value="Technical">Technical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-sonas-dim uppercase tracking-widest flex items-center gap-2">
                        AI Style {!isPro && <Crown size={10} className="text-sonas-accent" />}
                      </label>
                      <Select value={formData.ai_style} onValueChange={val => setFormData({...formData, ai_style: val})}>
                        <SelectTrigger className="bg-sonas-surface2 border-white/5 h-10 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-sonas-bg border-white/10">
                          <SelectItem value="Concise">Concise</SelectItem>
                          <SelectItem value="Detailed">Detailed</SelectItem>
                          <SelectItem value="Experimental">Experimental</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-sonas-dim uppercase tracking-widest flex items-center gap-2">
                      Base Instructions {!isPro && <Crown size={10} className="text-sonas-accent" />}
                    </label>
                    <Textarea 
                      value={formData.ai_instructions} 
                      onChange={e => setFormData({...formData, ai_instructions: e.target.value})} 
                      placeholder={isPro ? "e.g. Always use task.wait() instead of wait()" : "Upgrade to Pro to customize instructions."}
                      className="bg-sonas-surface2 border-white/5 rounded-xl text-xs h-24" 
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Platform Controls */}
            <section className="space-y-4">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-blue-500 flex items-center gap-2">
                <Cpu size={12} /> Sync & Commms
              </h3>
              <div className="space-y-2">
                <div className={cn("flex items-center justify-between p-4 bg-white/[0.02] border border-white/5 rounded-2xl", !isPro && "opacity-50")}>
                  <div>
                    <p className="text-xs font-bold flex items-center gap-2">
                      Auto-Sync Heartbeat {!isPro && <Crown size={12} className="text-sonas-accent" />}
                    </p>
                    <p className="text-[9px] text-sonas-muted uppercase tracking-tighter">Maintain persistent Studio connection</p>
                  </div>
                  <Switch 
                    disabled={!isPro}
                    checked={formData.auto_sync_enabled} 
                    onCheckedChange={v => setFormData({...formData, auto_sync_enabled: v})} 
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                  <div>
                    <p className="text-xs font-bold">Platform Broadcasts</p>
                    <p className="text-[9px] text-sonas-muted uppercase tracking-tighter">Receive system alerts and updates</p>
                  </div>
                  <Switch 
                    checked={formData.notifications_enabled} 
                    onCheckedChange={v => setFormData({...formData, notifications_enabled: v})} 
                  />
                </div>
              </div>
            </section>
          </div>

          <div className="p-6 bg-sonas-bg border-t border-white/5 sticky bottom-0 z-20">
            <Button 
              className="w-full h-12 rounded-2xl bg-sonas-accent text-sonas-bg font-bold shadow-lg shadow-sonas-accent/20 hover:opacity-90 transition-all" 
              onClick={handleSave} 
              disabled={loading || isAccountRestricted}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Commit Workspace Changes
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AccountSettingsSheet;